import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import { Stripe } from "stripe";

import useWLPromoStore from "./store";
import { bugNotif } from "../../api/bugsnag";
import { isPromoValid } from "../../services/promo";
import { getErrorMsg } from "../../../Common/utils/common";
import { validateStripePromo } from "../../api/stripe";
import { WLFormInputProps } from "../../../Common/components/WLForms/WLFormInput/WLFormInput";
import { ViewWLSubscriptionsTiers } from "../../../Common/models/subscriptions";
import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { PromoTypeConfig } from "../../../Common/models/promos";
import { useWebUserStore } from "../../CommonStores/user/users";
import { EN_TEXT_ERRORS } from "../../../Common/constants/error";

export const useWLPromoFieldProvider = (
  selectedSubscription: ViewWLSubscriptionsTiers | null,
  promoConfig: PromoTypeConfig,
  powerUpUsingPoints?: boolean
) => {
  const {
    setValidPromoCode,
    setValidWLPromo,

    setValidCouponCode,
    setValidStripeCoupon,

    setPromoValidating,
  } = useWLPromoStore();

  const useFormMethods = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { clearErrors, setError } = useFormMethods;

  const validatePromo = useCallback(
    debounce(async (code: string | null | undefined) => {
      setPromoValidating(true);
      if (code === undefined || isEmpty(code) || isNull(code)) {
        setValidWLPromo(null);
        setValidPromoCode("");
        setValidCouponCode("");
        setValidStripeCoupon(null);
        clearErrors();
        return true;
      } else {
        setValidWLPromo(undefined);
        setValidPromoCode(undefined);
        setValidCouponCode(undefined);
        setValidStripeCoupon(undefined);
        const [isValidWLPromo, isValidStripePromo] = await Promise.all([
          await validateWLPromoCode(code, promoConfig),
          await validateStripeCoupon(code, selectedSubscription),
        ]);

        setPromoValidating(false);

        if (!isValidWLPromo && !isValidStripePromo) {
          console.log("should set error");
          setError(promoField.name, {
            message: EN_TEXT_ERRORS.invalidPromoCodeError,
          });
        } else {
          clearErrors();
        }

        return (
          isValidWLPromo ||
          isValidStripePromo ||
          EN_TEXT_ERRORS.invalidPromoCodeError
        );
      }
    }, 400),
    []
  );

  const validateWLPromoCode = useCallback(
    async (code: string, promoConfig: PromoTypeConfig) => {
      try {
        const result = await isPromoValid({
          code,
          ...promoConfig,
        });
        const testedPromoCode = !isNull(result) ? result.code : "";
        console.log("VALIDATING --- result ---- ", result);
        setValidWLPromo(result);
        setValidPromoCode(testedPromoCode);

        // console.log("got valid promo!", testedPromoCode);
        return !isEmpty(testedPromoCode);
      } catch (error) {
        setValidWLPromo(null);
        setValidPromoCode("");
        bugNotif("validateWLPromoCode", getErrorMsg(error).rawErrorMsg);
        return false;
      }
    },
    []
  );

  const validateStripeCoupon = useCallback(
    async (
      promoCode: string,
      selectedSubscription: ViewWLSubscriptionsTiers | null
    ) => {
      try {
        const user = useWebUserStore.getState().user;

        if (
          (!!user && !user.stripeId) ||
          (promoConfig.forPowerUp && powerUpUsingPoints)
        ) {
          setValidStripeCoupon(null);
          return false;
        }

        const result = await validateStripePromo({
          promoCode,
        });
        const coupon = result.data.coupon as Stripe.Coupon;
        // console.log("got valid stripe promo!", coupon);
        if (
          coupon.applies_to !== undefined &&
          !isNull(selectedSubscription) &&
          coupon.applies_to.products.indexOf(selectedSubscription.docId) === -1
        ) {
          setValidStripeCoupon(null);
          return false;
        }

        setValidStripeCoupon(coupon);
        setValidCouponCode(promoCode);

        return true;
      } catch (error) {
        setValidStripeCoupon(null);
        setValidCouponCode("");
        bugNotif(
          "validateWLPromoAndStripeCoupon",
          getErrorMsg(error).rawErrorMsg
        );
        return false;
      }
    },
    []
  );

  const promoField: WLFormInputProps = {
    label: "Add promo code",
    name: "promoCode",
    type: "text",
    // disabled: validPromoCode === undefined || validStripeCoupon === undefined,
    className: "ion-text-start",
    placeholder: "Add promo code",
    allCaps: true,
    darkMode: promoConfig.forPowerUp,
    customOnChange: validatePromo,
    rules: {
      validate: async (data) => {
        const code = data.trim().toUpperCase();
        const validationResult = await validatePromo(code);
        console.log("returning validation", validationResult);
        return validationResult;
      },
    },
  };

  return {
    promoField,
    useFormMethods,
    validatePromo,
  };
};
